import React, { useEffect, useRef } from 'react';
import { useAuth } from '../auth/useAuth';
import { ajax } from '../hooks/useApi';
import { useQuery } from '@tanstack/react-query';
import { AppContext } from '../hooks/useConfig';
import { useLocation, useNavigate } from 'react-router-dom';

interface Permissions {
  menu_id: string;
  can_access: boolean;
  can_create: boolean;
  can_update: boolean;
  can_delete: boolean;
};

export interface Config {
  [key: string]: any;
}

export const AppProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const loginRequiredRef = useRef(false);
  const isInAppRoute = location.pathname.startsWith('/app') || location.pathname.startsWith('/legal');

  const { data: config, refetch, error, isFetched } = useQuery<any>({
    queryKey: ['user-config', user],
    queryFn: () => ajax('POST', '/user/config'),
    enabled: !!user && isInAppRoute,
    staleTime: Infinity,
    select: response => {
      if (response.status === "error" && response.token === "not-able-to-get-user-configuration") {
        loginRequiredRef.current = true;
      }
      return response?.data?.user || null
    }
  });

  useEffect(() => {
    if (isFetched && !error && !config) {
      loginRequiredRef.current = true;
    }
  }, [config, error, isFetched])

  useEffect(() => {
    if (loginRequiredRef.current) {
      console.log("navigate to login");
      navigate("/login");
    }
  }, [navigate]);

  const getPermissions = (keyAccess: string): Permissions => {
    if (config?.isNex) {
      return {
        menu_id: keyAccess,
        can_access: true,
        can_create: true,
        can_update: true,
        can_delete: true,
      };  
    }
    const findMenu = config?.permissions?.find((m: any) => m.menu_id === keyAccess);
    const haveAccess = !!findMenu?.can_access || false;
    return {
      menu_id: findMenu?.menu_id || keyAccess,
      can_access: haveAccess,
      can_create: haveAccess && !!findMenu?.can_create || false,
      can_update: haveAccess && !!findMenu?.can_update || false,
      can_delete: haveAccess && !!findMenu?.can_delete || false,
    };
  };

  const getLicenseLimits = () => {
    let defaultLimits = {
      users: 0,
      clinics: 0,
      patients: 0,
    };
    if (config?.licenses?.length > 0) {
      defaultLimits = {
        users: Number(config?.licenses?.[0].max_users) + Number(config?.packages.users),
        clinics: Number(config?.licenses?.[0].max_clinics) + Number(config?.packages.clinics),
        patients: Number(config?.licenses?.[0].max_patients) + Number(config?.packages.patients),
      };
    }
    return defaultLimits;
  };

  return <AppContext.Provider value={{
    config: config || null,
    getPermissions,
    getLicenseLimits,
    refetchConfig: refetch,
  }}>{children}</AppContext.Provider>;
};
